import { useState, useCallback } from "react";
import {
  Users, LogOut, Search, Plus, Trash2, Wallet, Stamp,
  ChevronLeft, ChevronRight, Baby, User, Calendar, X, Check, Mail,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  const d = new Date(year, month - 1, day);
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const months = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  const nth = (n) => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) { case 1: return "st"; case 2: return "nd"; case 3: return "rd"; default: return "th"; }
  };
  return `${days[d.getDay()]}, ${d.getDate()}${nth(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const SPAdmin = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [pin, setPin] = useState("");
  const [staffPin, setStaffPin] = useState("");
  const [tab, setTab] = useState("members");

  // Members state
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newForm, setNewForm] = useState({ full_name: "", email: "" });
  const [createdResult, setCreatedResult] = useState(null);
  const [actionMember, setActionMember] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionAmount, setActionAmount] = useState("");
  const [actionDesc, setActionDesc] = useState("");

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [sessionDetail, setSessionDetail] = useState(null);

  const totalRevenue = sessions.reduce((s, x) => s + x.total_revenue, 0);

  const loadMembers = useCallback(async (p) => {
    try {
      const res = await axios.get(`${API}/stayandplay/admin/members?pin=${p}`);
      setMembers(res.data);
    } catch { toast.error("Failed to load members"); }
  }, []);

  const loadSessions = useCallback(async (p) => {
    try {
      const res = await axios.get(`${API}/stayandplay/admin/sessions?pin=${p}`);
      setSessions(res.data);
    } catch { toast.error("Failed to load sessions"); }
  }, []);

  const handleLogin = async () => {
    try {
      await axios.post(`${API}/stayandplay/staff/login`, { pin });
      setLoggedIn(true);
      setStaffPin(pin);
      loadMembers(pin);
      loadSessions(pin);
    } catch { toast.error("Invalid PIN"); }
  };

  const resendCard = async (member) => {
    try {
      await axios.post(`${API}/stayandplay/admin/members/${member.id}/resend-card?pin=${staffPin}`);
      toast.success(`Card resent to ${member.email}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to resend"); }
  };

  const filteredMembers = members.filter((m) => {
    if (!memberSearch) return true;
    const q = memberSearch.toLowerCase();
    return (
      m.full_name.toLowerCase().includes(q) ||
      m.email.toLowerCase().includes(q) ||
      (m.mobile || "").includes(q)
    );
  });

  const addMember = async () => {
    if (!newForm.full_name.trim() || !newForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    try {
      const res = await axios.post(`${API}/stayandplay/admin/members?pin=${staffPin}`, newForm);
      setCreatedResult(res.data);
      setShowAddModal(false);
      setNewForm({ full_name: "", email: "" });
      loadMembers(staffPin);
      toast.success("Member added!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add member"); }
  };

  const deleteMember = async (member) => {
    if (!window.confirm(`Remove ${member.full_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/stayandplay/admin/members/${member.id}?pin=${staffPin}`);
      toast.success("Member removed");
      loadMembers(staffPin);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const adjustWallet = async (isAdd) => {
    const amt = parseFloat(actionAmount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      const res = await axios.post(`${API}/stayandplay/staff/wallet/adjust?pin=${staffPin}`, {
        account_id: actionMember.id,
        amount: isAdd ? amt : -amt,
        description: actionDesc || `Staff ${isAdd ? "top-up" : "deduction"}`,
      });
      toast.success(`Wallet ${isAdd ? "topped up" : "deducted"}: £${amt.toFixed(2)}`);
      setMembers((prev) =>
        prev.map((m) => m.id === actionMember.id ? { ...m, wallet_balance: res.data.new_balance } : m)
      );
      setActionMember(null); setActionType(null); setActionAmount(""); setActionDesc("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const adjustStamps = async (isAdd) => {
    const amt = parseInt(actionAmount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid number"); return; }
    try {
      const res = await axios.post(`${API}/stayandplay/staff/stamps/adjust?pin=${staffPin}`, {
        account_id: actionMember.id,
        amount: isAdd ? amt : -amt,
      });
      toast.success("Stamps updated");
      setMembers((prev) =>
        prev.map((m) => m.id === actionMember.id ? { ...m, loyalty_stamps: res.data.new_stamps } : m)
      );
      setActionMember(null); setActionType(null); setActionAmount(""); setActionDesc("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const loadSessionDetail = async (dateStr) => {
    try {
      const res = await axios.get(`${API}/stayandplay/admin/sessions/${dateStr}?pin=${staffPin}`);
      setSessionDetail(res.data);
    } catch { toast.error("Failed to load session details"); }
  };

  // ===== LOGIN =====
  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-lg max-w-sm w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#ff3131]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-[#ff3131]" />
            </div>
            <h1 className="text-2xl font-bold text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>
              Admin Panel
            </h1>
            <p className="text-sm text-[#636E72] mt-1">Stay &amp; Play Management</p>
          </div>
          <input
            type="password" maxLength={4} value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            placeholder="••••"
            className="w-full text-center text-3xl tracking-[0.5em] p-4 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] outline-none mb-4"
            data-testid="sp-admin-pin"
          />
          <button
            onClick={handleLogin}
            className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white py-3 rounded-full font-bold transition-colors"
            style={{ fontFamily: "Fredoka, cursive" }}
            data-testid="sp-admin-login-btn"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  // ===== MAIN =====
  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="sp-admin-panel">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>
              Stay &amp; Play Admin
            </h1>
            <p className="text-xs text-[#636E72]">
              {members.length} members &middot; {sessions.length} sessions &middot; £{totalRevenue.toFixed(2)} total revenue
            </p>
          </div>
          <button
            onClick={() => setLoggedIn(false)}
            className="p-2 text-[#636E72] hover:text-[#ff3131] transition-colors"
            data-testid="sp-admin-logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 pt-4 pb-12">
        {/* Tab bar */}
        <div className="flex gap-1 bg-white rounded-xl p-1 mb-5 shadow-sm">
          {[
            { id: "members", label: "Members", icon: Users },
            { id: "sessions", label: "Sessions", icon: Calendar },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setSessionDetail(null); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors ${tab === t.id ? "bg-[#ff3131] text-white" : "text-[#636E72] hover:text-[#2D3436]"}`}
              data-testid={`sp-admin-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {/* ===== MEMBERS TAB ===== */}
        {tab === "members" && (
          <div>
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#636E72]" />
                <input
                  type="text" value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Search name, email or mobile..."
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] outline-none text-sm bg-white"
                  data-testid="sp-admin-member-search"
                />
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 bg-[#ff3131] hover:bg-[#e62e2e] text-white px-4 py-3 rounded-xl font-bold text-sm transition-colors"
                data-testid="sp-admin-add-member-btn"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>

            <p className="text-xs text-[#636E72] mb-3">
              {filteredMembers.length} member{filteredMembers.length !== 1 ? "s" : ""}
            </p>

            <div className="space-y-2">
              {filteredMembers.map((m) => (
                <div key={m.id} className="bg-white rounded-xl p-4 shadow-sm" data-testid={`sp-admin-member-${m.id}`}>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full bg-[#ff3131] text-white flex items-center justify-center font-bold text-sm flex-shrink-0"
                      style={{ fontFamily: "Fredoka, cursive" }}
                    >
                      {m.full_name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#2D3436] text-sm" style={{ fontFamily: "Fredoka, cursive" }}>
                        {m.full_name}
                      </p>
                      <p className="text-xs text-[#636E72] truncate">
                        {m.email}{m.mobile ? ` · ${m.mobile}` : ""}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs font-semibold text-[#ff3131]">£{m.wallet_balance?.toFixed(2)} wallet</span>
                        <span className="text-xs text-[#636E72]">{m.loyalty_stamps} stamps</span>
                        <span className="text-[10px] font-mono text-[#636E72]">{m.qr_code}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => resendCard(m)}
                        className="p-2 rounded-lg bg-gray-100 text-[#636E72] hover:bg-[#ff3131]/10 hover:text-[#ff3131] transition-colors"
                        title="Resend membership card email"
                        data-testid={`sp-admin-resend-btn-${m.id}`}
                      >
                        <Mail className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { setActionMember(m); setActionType("wallet"); setActionAmount(""); setActionDesc(""); }}
                        className="p-2 rounded-lg bg-[#7dbff8]/10 text-[#7dbff8] hover:bg-[#7dbff8]/20 transition-colors"
                        title="Adjust wallet"
                        data-testid={`sp-admin-wallet-btn-${m.id}`}
                      >
                        <Wallet className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { setActionMember(m); setActionType("stamps"); setActionAmount(""); setActionDesc(""); }}
                        className="p-2 rounded-lg bg-[#ff3131]/10 text-[#ff3131] hover:bg-[#ff3131]/20 transition-colors"
                        title="Adjust stamps"
                        data-testid={`sp-admin-stamps-btn-${m.id}`}
                      >
                        <Stamp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteMember(m)}
                        className="p-2 rounded-lg bg-gray-100 text-[#636E72] hover:bg-red-100 hover:text-red-500 transition-colors"
                        title="Remove member"
                        data-testid={`sp-admin-delete-btn-${m.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {filteredMembers.length === 0 && (
                <div className="text-center py-12 text-[#636E72] text-sm">
                  {memberSearch ? "No members match your search" : "No members yet — add your first member above"}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== SESSIONS TAB ===== */}
        {tab === "sessions" && (
          <div>
            {sessionDetail ? (
              /* Session detail */
              <div data-testid="sp-admin-session-detail">
                <button
                  onClick={() => setSessionDetail(null)}
                  className="flex items-center gap-1 text-[#636E72] hover:text-[#2D3436] mb-4 text-sm font-semibold transition-colors"
                  data-testid="sp-admin-session-back"
                >
                  <ChevronLeft className="w-4 h-4" /> Back to sessions
                </button>

                <h2 className="text-xl font-bold text-[#2D3436] mb-4" style={{ fontFamily: "Fredoka, cursive" }}>
                  {formatDate(sessionDetail.date)}
                </h2>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-white rounded-xl p-4 shadow-sm text-center col-span-2 sm:col-span-1">
                    <p className="text-3xl font-bold text-[#ff3131]" style={{ fontFamily: "Fredoka, cursive" }}>
                      £{sessionDetail.summary.total_revenue.toFixed(2)}
                    </p>
                    <p className="text-xs text-[#636E72] mt-1">Total Revenue</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm text-center col-span-2 sm:col-span-1">
                    <p className="text-3xl font-bold text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>
                      {sessionDetail.summary.total_visits}
                    </p>
                    <p className="text-xs text-[#636E72] mt-1">Families attended</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                    <Baby className="w-5 h-5 text-[#ff3131] mx-auto mb-1" />
                    <p className="text-2xl font-bold text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>
                      {sessionDetail.summary.total_children}
                    </p>
                    <p className="text-xs text-[#636E72]">Children</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                    <User className="w-5 h-5 text-[#7dbff8] mx-auto mb-1" />
                    <p className="text-2xl font-bold text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>
                      {sessionDetail.summary.total_adults}
                    </p>
                    <p className="text-xs text-[#636E72]">Adults</p>
                  </div>
                </div>

                {/* Payment breakdown */}
                <div className="bg-white rounded-xl p-4 shadow-sm mb-5">
                  <h3 className="font-bold text-[#2D3436] mb-3 text-sm" style={{ fontFamily: "Fredoka, cursive" }}>
                    Payment Breakdown
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: "Wallet", value: sessionDetail.summary.wallet_used, color: "text-[#7dbff8]" },
                      { label: "Cash", value: sessionDetail.summary.cash_paid, color: "text-green-600" },
                      { label: "Card", value: sessionDetail.summary.card_paid, color: "text-[#2D3436]" },
                    ].map((item) => (
                      <div key={item.label} className="bg-[#FFFBF5] rounded-lg p-3 text-center">
                        <p className="text-xs text-[#636E72] mb-1">{item.label}</p>
                        <p className={`font-bold ${item.color}`}>£{item.value.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Attendees */}
                <h3 className="font-bold text-[#2D3436] mb-2 text-sm" style={{ fontFamily: "Fredoka, cursive" }}>
                  Attendees ({sessionDetail.visits.length})
                </h3>
                <div className="space-y-2">
                  {sessionDetail.visits.map((v) => (
                    <div key={v.id} className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-[#2D3436] text-sm">{v.member_name}</p>
                          <p className="text-xs text-[#636E72]">
                            {v.num_children} child{v.num_children !== 1 ? "ren" : ""} &middot;{" "}
                            {v.num_adults} adult{v.num_adults !== 1 ? "s" : ""} &middot;{" "}
                            <span className="capitalize">{v.payment_method}</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[#2D3436]">£{v.total.toFixed(2)}</p>
                          {v.wallet_used > 0 && (
                            <p className="text-xs text-green-600">-£{v.wallet_used.toFixed(2)} wallet</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Sessions list */
              <div>
                {sessions.length === 0 ? (
                  <div className="text-center py-12 text-[#636E72] text-sm">
                    No sessions recorded yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sessions.map((s) => (
                      <button
                        key={s.date}
                        onClick={() => loadSessionDetail(s.date)}
                        className="w-full bg-white rounded-xl p-4 shadow-sm text-left hover:shadow-md transition-shadow"
                        data-testid={`sp-admin-session-${s.date}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>
                              {formatDate(s.date)}
                            </p>
                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                              <span className="text-xs text-[#636E72] flex items-center gap-1">
                                <Baby className="w-3 h-3" />{s.total_children} children
                              </span>
                              <span className="text-xs text-[#636E72] flex items-center gap-1">
                                <User className="w-3 h-3" />{s.total_adults} adults
                              </span>
                              <span className="text-xs text-[#636E72]">{s.total_visits} families</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-xl font-bold text-[#ff3131]" style={{ fontFamily: "Fredoka, cursive" }}>
                              £{s.total_revenue.toFixed(2)}
                            </p>
                            <ChevronRight className="w-4 h-4 text-[#636E72]" />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ADD MEMBER MODAL */}
      {showAddModal && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>Add Member</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1 text-[#636E72] hover:text-[#2D3436]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 mb-4">
              <input
                type="text" value={newForm.full_name}
                onChange={(e) => setNewForm((p) => ({ ...p, full_name: e.target.value }))}
                placeholder="Full name *"
                className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] outline-none text-sm"
                data-testid="sp-admin-new-name"
              />
              <input
                type="email" value={newForm.email}
                onChange={(e) => setNewForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="Email address *"
                className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] outline-none text-sm"
                data-testid="sp-admin-new-email"
              />
            </div>
            <button
              onClick={addMember}
              className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white py-3 rounded-full font-bold transition-colors"
              style={{ fontFamily: "Fredoka, cursive" }}
              data-testid="sp-admin-create-member-btn"
            >
              Create Member
            </button>
          </div>
        </div>
      )}

      {/* CREATED MEMBER SUCCESS MODAL */}
      {createdResult && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => setCreatedResult(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-bold text-xl text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>Member Created!</h3>
            </div>
            <div className="bg-[#FFFBF5] rounded-xl p-4 mb-4 space-y-2.5 text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-[#636E72]">Name</span>
                <span className="font-semibold text-[#2D3436] text-right">{createdResult.full_name}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#636E72]">Email</span>
                <span className="font-semibold text-[#2D3436] text-right break-all">{createdResult.email}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#636E72]">QR Code</span>
                <span className="font-mono text-[#2D3436]">{createdResult.qr_code}</span>
              </div>
            </div>
            <p className="text-xs text-[#636E72] text-center mb-4">
              The membership card has been emailed to the member.
            </p>
            <button
              onClick={() => setCreatedResult(null)}
              className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white py-3 rounded-full font-bold transition-colors"
              style={{ fontFamily: "Fredoka, cursive" }}
              data-testid="sp-admin-created-done-btn"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* WALLET / STAMPS ACTION MODAL */}
      {actionMember && actionType && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4"
          onClick={() => { setActionMember(null); setActionType(null); }}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>
                {actionType === "wallet" ? "Adjust Wallet" : "Adjust Stamps"}
              </h3>
              <button onClick={() => { setActionMember(null); setActionType(null); }} className="p-1 text-[#636E72]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-[#636E72] mb-4">
              <span className="font-semibold text-[#2D3436]">{actionMember.full_name}</span> &mdash; Current:{" "}
              <strong className="text-[#2D3436]">
                {actionType === "wallet" ? `£${actionMember.wallet_balance?.toFixed(2)}` : `${actionMember.loyalty_stamps} stamps`}
              </strong>
            </p>
            <input
              type="number"
              step={actionType === "wallet" ? "0.50" : "1"}
              min="0"
              value={actionAmount}
              onChange={(e) => setActionAmount(e.target.value)}
              placeholder={actionType === "wallet" ? "Amount (£)" : "Number of stamps"}
              className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] outline-none mb-3 text-sm"
              data-testid="sp-admin-action-amount"
            />
            {actionType === "wallet" && (
              <input
                type="text" value={actionDesc}
                onChange={(e) => setActionDesc(e.target.value)}
                placeholder="Description (optional)"
                className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] outline-none mb-4 text-sm"
                data-testid="sp-admin-action-desc"
              />
            )}
            {actionType === "stamps" && <div className="mb-4" />}
            <div className="flex gap-2">
              {actionType === "wallet" ? (
                <>
                  <button onClick={() => adjustWallet(true)} className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 rounded-full font-bold text-sm transition-colors" data-testid="sp-admin-wallet-add">
                    Top Up
                  </button>
                  <button onClick={() => adjustWallet(false)} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-full font-bold text-sm transition-colors" data-testid="sp-admin-wallet-deduct">
                    Deduct
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => adjustStamps(true)} className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 rounded-full font-bold text-sm transition-colors" data-testid="sp-admin-stamps-add">
                    Add Stamps
                  </button>
                  <button onClick={() => adjustStamps(false)} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-full font-bold text-sm transition-colors" data-testid="sp-admin-stamps-remove">
                    Remove
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SPAdmin;
